import Pusher from 'pusher-js';
import type { ChannelAuthorizationCallback, ChannelAuthorizationData } from 'pusher-js/types/src/core/auth/options';
import { getAccessToken } from 'src/utils/tokenService';
import { captureException, captureMessage } from '@sentry/react';
import { realtimeLog } from 'src/utils/logger';

// Shared Laravel Reverb (Pusher protocol) singleton so the SPA holds ONE websocket connection.
// Owns the connection, the Auth0 channel-auth handshake, and a reconnect hook (events during a
// transient drop aren't buffered, so features must re-fetch on reconnect).

let pusherSingleton: Pusher | null = null;
const reconnectListeners = new Set<() => void>();

/** Register a callback fired when the connection RE-connects (not on first connect). Returns an unregister fn. */
export function onPusherReconnect(cb: () => void): () => void {
  reconnectListeners.add(cb);
  return () => {
    reconnectListeners.delete(cb);
  };
}

function reverbHost(): string {
  // Prefer BASE_URL's host when absolute (dev points at a remote Reverb); fall back to
  // window.location.host in prod, where the SPA shares the host with Reverb.
  const baseUrl = process.env.BASE_URL;
  if (baseUrl && baseUrl !== '/' && /^https?:\/\//i.test(baseUrl)) {
    try {
      return new URL(baseUrl).host;
    } catch {
      // fall through
    }
  }
  if (typeof window !== 'undefined' && window.location) {
    return window.location.host;
  }
  return '';
}

async function authoriseChannel(
  socketId: string,
  channelName: string,
  callback: ChannelAuthorizationCallback,
): Promise<void> {
  try {
    const token = await getAccessToken();
    if (!token) {
      callback(new Error('no access token'), null);
      return;
    }
    const res = await fetch(`${process.env.BASE_URL}api/public/broadcasting/auth`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ socket_id: socketId, channel_name: channelName }),
    });
    if (!res.ok) {
      realtimeLog.warn(`broadcasting/auth ${res.status}`);
      captureMessage('reverb channel auth rejected', {
        level: 'warning',
        tags: { context: 'reverb', http_status: String(res.status) },
      });
      callback(new Error(`broadcasting/auth ${res.status}`), null);
      return;
    }
    callback(null, (await res.json()) as ChannelAuthorizationData);
  } catch (e) {
    realtimeLog.warn('channel auth threw', e);
    captureException(e instanceof Error ? e : new Error(String(e)), {
      tags: { context: 'reverb', phase: 'channel_auth' },
    });
    callback(e instanceof Error ? e : new Error(String(e)), null);
  }
}

/** The shared Pusher/Reverb singleton, or null when env (REVERB_APP_KEY / host) is missing. */
export function getPusher(): Pusher | null {
  if (pusherSingleton) return pusherSingleton;

  const key = process.env.REVERB_APP_KEY;
  const host = reverbHost();
  if (!key || !host) {
    realtimeLog.warn('REVERB_APP_KEY or host missing; live updates disabled');
    return null;
  }

  pusherSingleton = new Pusher(key, {
    wsHost: host,
    wsPort: 443,
    wssPort: 443,
    forceTLS: true,
    // Must include 'ws' (forceTLS upgrades it to wss://); 'wss'-only leaves no resolvable transport.
    enabledTransports: ['ws', 'wss'],
    cluster: 'mt1', // required by pusher-js at construction; ignored by Reverb.
    channelAuthorization: {
      // customHandler resolves the JWT fresh per POST; a static header would freeze an expiring token.
      customHandler: (params, callback) => {
        void authoriseChannel(params.socketId, params.channelName, callback);
      },
    },
  });

  pusherSingleton.connection.bind('error', (err: unknown) => {
    realtimeLog.warn('connection error', err);
  });

  let wasConnected = false;
  pusherSingleton.connection.bind('state_change', ({ current }: { current: string }) => {
    if (current === 'connected' && wasConnected) reconnectListeners.forEach((cb) => cb());
    if (current === 'connected') wasConnected = true;
  });

  return pusherSingleton;
}

/** Unsubscribe a channel on the shared singleton (no-op if the singleton was never created). */
export function unsubscribeChannel(channelName: string): void {
  pusherSingleton?.unsubscribe(channelName);
}
